import { useCallback, useEffect, useRef, useState } from "react";

/** Guard one asynchronous action until it settles, including same-tick submissions. */
export function useAsyncAction() {
  const [pending, setPending] = useState(false);
  const inFlight = useRef<Promise<void> | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const run = useCallback((action: () => Promise<void>): Promise<void> => {
    if (inFlight.current) return inFlight.current;
    if (mounted.current) setPending(true);
    let complete!: () => void;
    let fail!: (error: unknown) => void;
    const completion = new Promise<void>((resolve, reject) => {
      complete = resolve;
      fail = reject;
    });
    const operation = completion.finally(() => {
      inFlight.current = null;
      if (mounted.current) setPending(false);
    });
    inFlight.current = operation;
    try {
      // Invoke immediately so form handlers prevent native submission before
      // the event returns, while the lock already blocks synchronous reentry.
      Promise.resolve(action()).then(complete, fail);
    } catch (error) {
      fail(error);
    }
    return operation;
  }, []);

  return { pending, run };
}
